/**
 * ActivityTrail — the audit's hash-chained event ledger, readable.
 *
 * GET /audits/:id/events already records every consequential action
 * (stage moves, letter sends, universe checks, overrides, closes) in an
 * append-only, hash-chained table; this card finally shows it. Each raw
 * action becomes a friendly sentence with its timestamp. Collapsed by
 * default so the right rail stays calm; fetches on open and refreshes
 * whenever the parent reloads the audit.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  STAGE_LABELS, SAMPLE_STATUS_LABELS, CONFERENCE_KIND_LABELS, ANALYSIS_KIND_LABELS,
  ANALYSIS_STATUS_LABELS, REPORT_KIND_LABELS, CLASSIFICATION_LABELS, CORRESPONDENCE_KIND_LABELS,
} from './labels.js';

const API = process.env.REACT_APP_API_URL;

function parseDetail(detail) {
  if (!detail) return {};
  if (typeof detail === 'string') { try { return JSON.parse(detail); } catch { return {}; } }
  return detail;
}

function sentence(ev) {
  const d = parseDetail(ev.detail);
  switch (ev.action) {
    case 'audit_created': return 'Audit created';
    case 'stage_changed':
      if (d.direction === 'back') return `Moved back to ${STAGE_LABELS[d.to] || d.to}${d.reason ? ` — “${d.reason}”` : ''}`;
      return `Moved to ${STAGE_LABELS[d.to] || d.to}`;
    case 'letter_generated': return 'Engagement notice drafted';
    case 'letter_sent':
      return d.via === 'email'
        ? `Engagement notice emailed to ${d.to || 'the auditee'}`
        : `Engagement notice marked as sent${d.to ? ` to ${d.to}` : ''}`;
    case 'universe_validated': return `Universe file checked${d.file_name ? ` — ${d.file_name}` : ''}`;
    case 'fieldwork_started': return 'Grounded review started';
    case 'criterion_overridden': return `Reviewer set a result to “${String(d.new_status || 'a new status').replace(/_/g, ' ')}”`;
    case 'findings_promoted': return 'Deficiencies raised as findings';
    case 'note_added': return 'Note added to the working file';
    case 'copilot_exchange': return 'Copilot consulted';
    case 'report_generated': return 'Report generated';
    case 'cap_created': return 'Corrective action plan opened';
    case 'audit_closed': return 'Audit wrapped up';
    case 'applied': return 'Protocol applied to this audit';
    case 'samples_selected': return `Sample drawn${d.total ? ` — ${d.total} cases (${d.random || 0} random, ${d.targeted || 0} targeted)` : ''}`;
    case 'sample_updated': return `Sample outcome recorded${d.status ? ` — ${SAMPLE_STATUS_LABELS[d.status] || d.status}` : ''}`;
    case 'disclosed_issue_recorded': return 'Pre-audit disclosed issue recorded';
    case 'disclosed_issue_removed': return 'Disclosed issue removed';
    case 'conference_recorded': return `${CONFERENCE_KIND_LABELS[d.kind] || 'Conference'} recorded`;
    case 'analysis_requested': return `${ANALYSIS_KIND_LABELS[d.kind] || 'Analysis'} requested${d.due_date ? `, due ${String(d.due_date).slice(0, 10)}` : ''}`;
    case 'analysis_updated': return `Analysis request updated — ${ANALYSIS_STATUS_LABELS[d.status] || d.status || 'changed'}`;
    case 'report_issued': return `${REPORT_KIND_LABELS[d.kind] || 'Report'} report issued`;
    case 'draft_comments_recorded': return 'Auditee comments recorded on the draft report';
    case 'condition_classified': return `Condition classified as ${CLASSIFICATION_LABELS[d.classification] || d.classification}`;
    case 'correspondence_sent':
      return d.via === 'email'
        ? `${CORRESPONDENCE_KIND_LABELS[d.kind] || 'Message'} emailed to ${d.to || 'the auditee'}`
        : `${CORRESPONDENCE_KIND_LABELS[d.kind] || 'Message'} logged as sent${d.to ? ` to ${d.to}` : ''}`;
    case 'universe_attempt': return `Universe submission attempt ${d.n || ''} recorded`.replace('  ', ' ');
    default: return String(ev.action || '').replace(/_/g, ' ');
  }
}

export default function ActivityTrail({ auditId, refreshKey = 0 }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null); // null = never loaded
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/events`, { credentials: 'include' }).then(r => r.json());
      if (r.success) setEvents(r.events || []);
    } catch { /* keep whatever we had */ }
    setLoading(false);
  }, [auditId]);

  // Fetch on first open; keep it fresh while open when the audit changes.
  useEffect(() => { if (open) load(); }, [open, refreshKey, load]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-left">
        <div className="text-sm font-semibold text-slate-800">Activity{events ? <span className="text-slate-400 font-normal"> · {events.length}</span> : null}</div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {!open && <div className="text-[11px] text-slate-400 mt-1">Everything that happened on this audit, in order, on a tamper-evident record.</div>}
      {open && (
        <div className="mt-3 space-y-2">
          {loading && !events && <div className="text-xs text-slate-400">Loading the activity trail…</div>}
          {events && events.length === 0 && <div className="text-xs text-slate-400">Nothing recorded yet.</div>}
          {(events || []).map(ev => (
            <div key={ev.id} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-orange-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[12px] text-slate-700">{sentence(ev)}</div>
                <div className="text-[10px] text-slate-400">
                  {ev.created_at ? new Date(ev.created_at).toLocaleString() : ''}
                  {ev.actor_name ? ` · ${ev.actor_name}` : ''}
                </div>
              </div>
            </div>
          ))}
          {events && events.length > 0 && (
            <div className="text-[10px] text-slate-400 pt-1 border-t border-slate-100">Hash-chained record — entries cannot be edited or deleted.</div>
          )}
        </div>
      )}
    </div>
  );
}
